'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  TraceEntityNavigationProvider as DesignSystemTraceEntityNavigationProvider,
  type TraceEntityNavigationTarget,
} from '@terra/ui-design-system';
import { useMainContentTabs } from '../../store/layoutStore';
import { buildMainContentTabFromChatObject } from '../../utils/chatMainContentTabUtils';

export function TraceEntityNavigationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { openOrFocusTab } = useMainContentTabs();
  const tTabTitles = useTranslations('chat.copilot.tabTitles');
  const tSkillEditor = useTranslations('customSkills.editor');

  const fallbackTitles = useMemo(
    () => ({
      finding: tTabTitles('findingDetails'),
      signal: tTabTitles('signalDetails'),
      endpoint: tTabTitles('endpoint'),
      webpage: tTabTitles('webPage'),
      execution: tTabTitles('execution'),
      skillEditor: tSkillEditor('createTitle'),
      requests: tTabTitles('requests'),
    }),
    [tSkillEditor, tTabTitles],
  );

  const handleOpenEntity = useCallback(
    (target: TraceEntityNavigationTarget) => {
      const tab = buildMainContentTabFromChatObject({
        target,
        fallbackTitles,
      });

      if (tab) {
        openOrFocusTab(tab);
      }
    },
    [fallbackTitles, openOrFocusTab],
  );

  return (
    <DesignSystemTraceEntityNavigationProvider onOpenEntity={handleOpenEntity}>
      {children}
    </DesignSystemTraceEntityNavigationProvider>
  );
}
